import { JsonError, Message, Session } from "../reducers/session";
import { api, OfflineStampRequest } from "../services";
import { timeClockAuthenticatePost } from "../../timeplan-api-client";
import { components as apiTypes } from "../../timeplan-api-schema";
import { deviceConnect, deviceUploadSignatures } from "../../../site-timeclock/actions/device";
import { getCoordinates } from "./stamp";

import * as moment from "moment";
import { AppDispatch } from "../../../views/common/store";
import { StampDirection, Config, AuthenticatedPerson, AuthenticationRequest, DeviceType } from "../models";
import * as enrollment from "./enrollment";
import { Action } from "../models/redux";
import { DateTime } from "luxon";

export type Dispatch = AppDispatch;

export const CANCEL = "CANCEL";

export const MESSAGE_DISMISS = "MESSAGE_DISMISS";
export const MESSAGE_SHOW = "MESSAGE_SHOW";

export const SESSION_INIT = "SESSION_INIT";
export const SESSION_REFRESH = "SESSION_REFRESH";

export const API_LOAD = "API_LOAD";
export const API_ERROR = "API_ERROR";
export const API_OFFLINE = "API_OFFLINE";
export const API_OFFLINE_APP = "APP_API_OFFLINE";

export const STAMP_BEGIN = "STAMP_BEGIN";
export const STAMP_FINGERPRINT_READ = "STAMP_FINGERPRINT_READ";
export const STAMP_POST = "STAMP_POST";
export const STAMP_CONFIRMED = "STAMP_CONFIRMED";

export const OFFLINE_STAMP = "OFFLINE_STAMP";
export const OFFLINE_CLEAR = "OFFLINE_CLEAR";

export const SELF_REGISTRATION_BEGIN = "SELF_REGISTRATION_BEGIN";

export const SETTINGS_OPEN = "SETTINGS_OPEN";
export const SETTINGS_SAVE = "SETTINGS_SAVE";

export const SCREENSAVER_POLL = "SCREENSAVER_POLL";
export const SCREENSAVER_RESET = "SCREENSAVER_RESET";

export const BALANCE_OPEN = "BALANCE_OPEN";
export const BACK_STAMP = "BACK_STAMP";

export const GEOPOSITION_UPDATE = "GEOPOSITION_UPDATE";
export const GEOPOSITION_ERROR = "GEOPOSITION_ERROR";

export const SET_DIRECTION = "SET_DIRECTION";

const UNKNOWN_RECONNECT_INTERVAL = 30 * 1000;
const OFFLINE_RECONNECT_INTERVAL = 90 * 1000;

export const TIMEOUT = 30 * 1000;

const luxonToMoment = (dateTime : DateTime) => moment(dateTime.toISO());

function handleRedirected(err) {
    if (err.response && err.response.redirected) {
        location.reload();
    }
}

function refreshSignatures() {
    return async (dispatch: Dispatch, getState) => {
        if (!getState().session.loginTimerId) {
            const newerThan = getState().signatures.lastUpdate.add(-5, "minute");

            try {
                const refreshResponse = await api.refreshSession(newerThan);
                await dispatch({ type: SESSION_REFRESH, refreshResponse });

                dispatch(deviceUploadSignatures());

                const { signatureRefreshSec } = getState().config;
                setTimeout(() => dispatch(refreshSignatures()), signatureRefreshSec);
            } catch (err) {
                if (err.response && err.response.status === 401) {
                    console.log("location.reload() due to status 401");
                    location.reload();
                }
                handleRedirected(err);
                console.log(err);
            }
        }
    };
}

function ping() {
    return async (dispatch: Dispatch, getState) => {
        const { session } = getState();
        if (!session.loginTimerId) {
            try {
                await api.ping(session.deviceSupport);
                const { heartBeatIntervalSec } = getState().config;
                setTimeout(() => dispatch(ping()), heartBeatIntervalSec * 1000);
            } catch (err) {
                handleRedirected(err);
                dispatch(apiOffline());
            }
        }
    };
}

export function timeout<T>(ms: number, promise: Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            reject(new Error("timeout"));
        }, ms);
        promise.then(resolve, reject);
    });
}

export function apiLoginTerminal() {
    return async (dispatch: Dispatch, getState: () => { config: Config, offline: OfflineStampRequest[] }): Promise<void> => {
        const { terminalGuid, signatureRefreshSec } = getState().config;

        try {
            const response = await timeout(TIMEOUT, api.terminalLogin(terminalGuid));
            if (response.heartBeatIntervalSec) {
                setTimeout(() => dispatch(ping()), response.heartBeatIntervalSec * 1000);
            }

            dispatch(settingsSave(response));

            const refreshResponse = await timeout(TIMEOUT, api.refreshSession());

            dispatch({ type: SESSION_INIT, refreshResponse });

            setTimeout(() => dispatch(refreshSignatures()), signatureRefreshSec);

            dispatch(deviceConnect());

            const offlineStamps = getState().offline;
            if (offlineStamps.length > 0) {
                await api.uploadOfflineStamps(offlineStamps);
                dispatch({ type: OFFLINE_CLEAR });
            }
        } catch (error) {
            if (!error.response) {
                dispatch(apiOffline(true));
            } else {
                dispatch({
                    loginTimerId: setTimeout(() => dispatch(apiLoginTerminal()), UNKNOWN_RECONNECT_INTERVAL),
                    message: error,
                    type: API_ERROR,
                });
            }
        }
    };
}

function apiOffline(forceFutureRetry = false) {
    return (dispatch, getState) => {
        if (!getState().session.loginTimerId || forceFutureRetry) {
            dispatch({
                loginTimerId: setTimeout(() => dispatch(apiLoginTerminal()), OFFLINE_RECONNECT_INTERVAL),
                type: API_OFFLINE,
            });
        }
    };
}

export function apiLoad(): Action {
    return { type: API_LOAD };
}

export function apiAuthenticationRequest(request: AuthenticationRequest) {
    return async (dispatch: Dispatch, getState: () => { session: Session }): Promise<void> => {
        dispatch(apiLoad());
        try {
            const session: Session = getState().session;
            const deviceSupport = (session.deviceSupport ? Object.keys(session.deviceSupport) : null) as DeviceType[];

            const authRequst = { ...request, deviceSupport };

            const json = await timeout(TIMEOUT, timeClockAuthenticatePost(authRequst));

            if (json.registrationReady) {
                for (const message of json.messages) {
                    dispatch(pushMessage(message));
                }

                dispatch(enrollment.begin(json.registrationReady));
            } else if (json.authenticatedPerson) {
                dispatch(stampBegin(json));
            }
        } catch (error) {
            if (!error.response) {
                dispatch(apiOffline());
                dispatch(stampBeginOffline(request.personRef, request.signature, request.code));
            } else {
                dispatch(setApiError("AuthenticationErrors", error));
                if (!error.jsonError) dispatch(stampBeginOffline(request.personRef, request.signature, request.code));
            }
        }
    };
}

export function pushMessage(message: Message): Action {
    return { type: MESSAGE_SHOW, message };
}

export function dismissMessage(): Action {
    return { type: MESSAGE_DISMISS };
}

export function cancel(): Action {
    return {
        type: CANCEL,
    };
}

export function setApiError(category: string, error: {jsonError: JsonError}) {
    if (error.jsonError) {
        return {
            systemMessage: { id: `${category}.${error.jsonError.type}`, values: error.jsonError.values },
            type: API_ERROR,
        };
    } else {
        return apiOffline();
    }
}

export function balanceView(person: AuthenticatedPerson, direction: string) {
    return (dispatch: Dispatch, getState: () => { session: Session }): void => {
        const session: Session = getState().session;

        if (session.loginTimerId) {
            clearTimeout(session.loginTimerId);
        }
        dispatch({ type: BALANCE_OPEN, person, direction, dispatch });
    };
}

export function settingsView() {
    return (dispatch: Dispatch, getState: () => { session: Session }): void => {
        const session: Session = getState().session;
        if (session.loginTimerId) {
            clearTimeout(session.loginTimerId);
        }
        dispatch({ type: SETTINGS_OPEN });
    };
}

export function settingsSave(config: Partial<Config>): {
    type: string;
    config: Partial<Config>;
} {
    if (config.serverUrl) {
        api.initialize(config.serverUrl);
    }

    return { type: SETTINGS_SAVE, config };
}

export function stampBegin(json: apiTypes["schemas"]["AuthenticationResponse"]) {
    return (dispatch: Dispatch, getState: () => { session: Session }): void => {
        const session: Session = getState().session;
        if (session.stampTimerId) {
            clearTimeout(session.stampTimerId);
        }

        const geolocationAvailable = "geolocation" in navigator;
        if (geolocationAvailable && json.authenticatedPerson.demandGeoposition != "Never") {
            navigator.geolocation.getCurrentPosition(
                (position: GeolocationPosition) => dispatch(geoPositionSuccess(position)),
                (error: GeolocationPositionError) => dispatch(geoPositionError(error)),
                { enableHighAccuracy: true }
            );
        }

        dispatch({
            messages: json.messages,
            stampPerson: json.authenticatedPerson,
            stampTimerId: setTimeout(() => {
                dispatch(cancel());
            }, 30 * 1000),
            type: STAMP_BEGIN,
        });
    };
}

function geoPositionSuccess(position: GeolocationPosition): Action {
    return { type: GEOPOSITION_UPDATE, position };
}

function geoPositionError(error: GeolocationPositionError): Action {
    return { type: GEOPOSITION_ERROR, error };
}

export function stampBeginOffline(personRef: number, signature: string, employeeNumber: string): (dispatch: Dispatch, getState: () => {
    session: Session;
}) => void {
    return stampBegin({
        authenticatedPerson: {
            demandFingerprint: false,
            demandGeoposition: "Never",
            employeeNumber,
            pendingStampTime: DateTime.now(),
            personName: "",
            personRef,
            stampInStatus: null,
            stampOperations: [],
            stampOutStatus: null,
            task: null,
            hasBalance: false,
            stampCostDivisions: [],
            lastStamp: null,
        },
        messages: [],
        registrationReady: null,
    });
}

export function stampComplete(
    person: AuthenticatedPerson,
    direction: StampDirection,
    deviationCodeRef: number,
    stampDate: moment.Moment,
    costDivisionRef: number,
    onStamp: () => unknown = null
) {
    return async (dispatch: Dispatch, getState: () => { session: Session }): Promise<void> => {
        const session: Session = getState().session;

        const pendingStampTime = luxonToMoment(person.pendingStampTime);

        if (!person.stampInStatus && !person.stampOutStatus) {
            dispatch(
                stampOffline({
                    direction: StampDirection[direction],
                    personCode: person.employeeNumber,
                    personRef: person.personRef,
                    signature: person.signature,
                    stampTime: pendingStampTime,
                })
            );
            dispatch(stampConfirmed());
        } else {
            try {
                stampDate = person.task ? luxonToMoment(person.task.date) : stampDate ? stampDate : pendingStampTime;

                dispatch({ type: STAMP_POST });
                await api.stamp({
                    costDivisionRef,
                    deviationCodeRef,
                    direction: StampDirection[direction],
                    personRef: person.personRef,
                    stampDate,
                    stampTime: moment(),
                    coordinates: getCoordinates(session.geoposition),
                });
                onStamp && (await onStamp());
                dispatch(stampConfirmed());
            } catch (error) {
                dispatch(setApiError("AuthenticationErrors", error));
            }
        }
    };
}

function stampOffline(stamp: OfflineStampRequest) {
    return { type: OFFLINE_STAMP, stamp };
}

function stampConfirmed() {
    return (dispatch) => {
        dispatch({ type: STAMP_CONFIRMED });
        setTimeout(() => {
            dispatch(dismissMessage());
        }, 2000);
    };
}

export function setDirection(direction: StampDirection): Action {
    return { type: SET_DIRECTION, payload: direction };
}
